import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { ads } from '@/api/endpoints';
import { canRewardAds, noAdReason } from '@/game/ads/availability';
import { nativeBridge, requestNativeRewardedAd } from '@/game/ads/nativeBridge';
import { useSession } from '@/store/session';
import { Icon, type IconName } from '@/components/ui/Icon';
import {
  Button, Empty, ErrorNote, Loading, Panel, Row, ScreenBody, ScreenHeader,
} from '@/components/ui/primitives';

type Offer = Awaited<ReturnType<typeof ads.hub>>['offers'][number];
type HistoryRow = Awaited<ReturnType<typeof ads.history>>['results'][number];

/**
 * The Reward Hub — the only place Shwe Kyat enters the economy.
 *
 * The client cannot grant a reward. It requests an impression, shows the ad, then
 * polls /ads/complete until the server has seen AdMob's signed SSV callback. A
 * skipped or unverified ad pays nothing, which is what makes the currency safe to
 * withdraw. See docs/ARCHITECTURE.md §3.
 */
export function RewardHub({ onBack }: { onBack: () => void }) {
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [playing, setPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const applyWallet = useSession((s) => s.applyWallet);
  const wallet = useSession((s) => s.wallet);
  const flags = useSession((s) => s.flags);

  /* See game/ads/availability.ts — an ad that cannot be verified must not be
     offered, however many offers the server is willing to list. */
  const adsCanReward = canRewardAds(flags);

  const load = useCallback(() => {
    void ads.hub().then((r) => { setOffers(r.offers); setEnabled(r.enabled); });
    void ads.history('ad').then((r) => setHistory(r.results));
  }, []);

  useEffect(load, [load]);

  /* Cooldown labels are server-computed, so re-fetch while the screen is open
     rather than counting down locally and drifting. */
  useEffect(() => {
    const t = setInterval(() => void ads.hub().then((r) => setOffers(r.offers)), 5000);
    return () => clearInterval(t);
  }, []);

  async function watch(offer: Offer) {
    setPlaying(offer.key);
    setError(null);
    try {
      /*
       * Inside the ShweTap app the shell owns playback: it holds the AdMob SDK, and AdMob
       * signs an SSV callback only for a native impression. It runs the same three legs as
       * the branch below — /ads/request, show, poll /ads/complete — under the same user.
       *
       * This screen exists on both, and it MUST be bridged rather than left on the web
       * path. `canRewardAds` now answers true inside the shell, so an unbridged hub would
       * list six live offers whose ads can never be verified: the exact "watch 30 seconds
       * for nothing" bug that hiding the CTA on web was introduced to fix.
       */
      const impressionUuid = nativeBridge()
        ? await playThroughShell(offer.key)
        : (await ads.request(offer.key)).impression_uuid;

      /* Asked again from here even when the shell already polled it, because the shell's
         answer carries no wallet and this screen shows a Kyat balance. It is safe to
         repeat: `grant_reward` is idempotent on the impression uuid and returns
         `already_granted`, so the second call reads the balance rather than minting. */
      const res = await ads.complete(impressionUuid);
      if (res.wallet) applyWallet(res.wallet);
      load();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(
          e.code === 'AD_NOT_VERIFIED'
            ? 'The ad network has not confirmed yet. Try again in a moment.'
            : e.message,
        );
      } else if (e instanceof AdAbandoned) {
        /* Closed early, or no fill. Not an error the player caused, and not silence:
           a spinner that just stops is indistinguishable from a broken button. */
        setError(e.message);
      } else setError('The ad could not be played.');
    } finally {
      setPlaying(null);
    }
  }

  if (!offers) return <Loading />;

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="ad" size={22} />} title="Reward Hub" onBack={onBack} />

      <Panel title="SHWE KYAT BALANCE">
        <p className="text-center font-pixel text-xl text-kyat-glow tabular">{wallet.kyat}</p>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
          Shwe Kyat is minted only by a completed rewarded ad — that is what makes it
          withdrawable. Every placement is capped per day so the payout stays bounded.
        </p>
      </Panel>

      {error && <ErrorNote>{error}</ErrorNote>}

      {!enabled ? (
        <Panel className="mt-3">
          <Empty>Ads are currently disabled. Check back later.</Empty>
        </Panel>
      ) : !adsCanReward ? (
        /*
         * The offers exist server-side and their caps are real, but nothing here
         * can pay on this platform: AdMob sends its SSV callback from the native
         * SDK only, so on the web /ads/complete answers AD_NOT_VERIFIED forever.
         * Listing a "Watch" offer here sent players into a 30-second ad for nothing.
         */
        <Panel className="mt-3">
          <Empty>{noAdReason(flags)}</Empty>
        </Panel>
      ) : (
        <Panel title="OFFERS" className="mt-3">
          {offers.map((o) => (
            <Row
              key={o.key}
              icon={<Icon name={iconFor(o.key)} />}
              title={o.name}
              subtitle={`${o.description} · ${o.remaining_today} left today`}
              right={
                <Button
                  variant="ad"
                  busy={playing === o.key}
                  disabled={!o.available || playing !== null}
                  onClick={() => void watch(o)}
                  className="shrink-0 px-3 py-2 text-xs"
                >
                  {o.available
                    ? <span className="inline-flex items-center gap-1"><Icon name="ad" size={13} /> Watch</span>
                    : o.reason || 'Unavailable'}
                </Button>
              }
            />
          ))}
        </Panel>
      )}

      <Panel title="HISTORY" className="mt-3">
        {history.length === 0 ? (
          <Empty>No rewards claimed yet.</Empty>
        ) : (
          history.slice(0, 12).map((h, i) => (
            <Row
              key={`${h.created_at}-${i}`}
              icon={<Icon name={h.currency === 'kyat' ? 'kyat' : h.currency === 'coin' ? 'coin' : 'gift'} />}
              title={h.title}
              subtitle={new Date(h.created_at).toLocaleString()}
              right={
                Number(h.amount) > 0 ? (
                  <span className={`shrink-0 font-bold tabular ${
                    h.currency === 'kyat' ? 'text-kyat-glow' : 'text-coin'}`}>
                    +{h.amount}
                  </span>
                ) : null
              }
            />
          ))
        )}
      </Panel>
    </ScreenBody>
  );
}

/** The player closed the ad early, or the SDK could not show one. Carries what to say. */
class AdAbandoned extends Error {}

/**
 * Play one rewarded ad through the app shell and return the impression it opened.
 *
 * Throws rather than returning a status because the caller's happy path is a single
 * straight line; a `dismissed` ad is genuinely the end of this attempt, not a value to
 * thread through. `unverified` is NOT a failure — the callback may simply be late, and
 * `/ads/complete` re-checks server-side.
 */
async function playThroughShell(placement: string): Promise<string> {
  const outcome = await requestNativeRewardedAd(placement);
  if (outcome.status === 'rewarded' || outcome.status === 'unverified') {
    return outcome.impressionUuid;
  }
  throw new AdAbandoned(
    outcome.status === 'dismissed'
      ? 'The ad was closed before it finished, so nothing was awarded.'
      : outcome.reason,
  );
}

function iconFor(key: string): IconName {
  if (key.includes('kyat')) return 'kyat';
  if (key.includes('double')) return 'coin';
  if (key.includes('mystery')) return 'gift';
  if (key.includes('stamina') || key.includes('torch')) return 'energy';
  if (key.includes('reveal')) return 'sparkle';
  if (key.includes('auto')) return 'auto';
  return 'ad';
}
